import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';

import type { RootState } from 'features/app/store/store';
import { ensureCallStateReset } from 'features/call/call-base/store/callSlice';
import { selectHasLingeringCallData } from 'features/call/call-base/store/callResetBoundarySlices';
import {
  readyForNextCall,
  resetToIdle,
  selectCallPageStatus,
} from 'features/call/call-ui-state/store';
import { CallPageStatus } from 'features/call/call-ui-state/enums';
import {
  sendAnalyticsInfo,
  sendAnalyticsWarning,
} from 'features/analytics/helpers';

const listenerMiddleware = createListenerMiddleware();

let isEnforcingCallStateReset = false;

listenerMiddleware.startListening({
  matcher: isAnyOf(resetToIdle, readyForNextCall),
  effect: async (_, listenerApi) => {
    if (isEnforcingCallStateReset) {
      return;
    }

    const state = listenerApi.getState() as RootState;
    const pageStatus = selectCallPageStatus(state);
    if (
      pageStatus !== CallPageStatus.IDLE ||
      !selectHasLingeringCallData(state)
    ) {
      listenerApi.dispatch(
        sendAnalyticsInfo({
          Method: 'CallStateResetBoundary',
          Message: 'No reset needed',
          pageStatus,
        })
      );
      return;
    }

    isEnforcingCallStateReset = true;

    try {
      await listenerApi.dispatch(ensureCallStateReset()).unwrap();
      listenerApi.dispatch(
        sendAnalyticsWarning({
          Method: 'CallStateResetBoundary',
          Message: 'Call state reset performed',
          pageStatus,
        })
      );
    } catch (error) {
      listenerApi.dispatch(
        sendAnalyticsWarning({
          Method: 'CallStateResetBoundary',
          Message: 'Call state reset failed',
          pageStatus,
          Error: String(error),
        })
      );
    } finally {
      isEnforcingCallStateReset = false;
    }
  },
});

export const callStateResetBoundaryMiddleware = listenerMiddleware.middleware;
